import { Spin } from 'antd'
import { isNumber } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { AnniversaryCardInfoFragment, useAnniversaryCardUpdateMutation } from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import s from './anniversary-card.module.less'
import { drawAnniversaryCard, getBackgroundColor, getFillStyle } from './draw-anniversary-card-util'

export const AnniversaryCardModalPage: React.FC<{
  onAnniversaryCardModalClose: () => void
  onPostEditorModalOpen: () => void
  postEditorReadyVisible: boolean
  postEditorPreReadyVisible: boolean
  anniversaryCards: AnniversaryCardInfoFragment[]
  backgroundUrl: string
  isViewMode: boolean
  canvasHeight: number
  canvasWidth: number
  imageHeight: number
  imageWidth: number
  anniversaryYears: number
}> = ({
  onAnniversaryCardModalClose,
  onPostEditorModalOpen,
  anniversaryCards,
  postEditorReadyVisible,
  postEditorPreReadyVisible,
  backgroundUrl,
  isViewMode,
  canvasWidth,
  canvasHeight,
  imageHeight,
  imageWidth,
  anniversaryYears,
}) => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = React.useState<number>()
  const [imageUrls, setImageUrls] = React.useState<string[]>([])
  const ref = React.useRef<HTMLImageElement>(null)
  const [cardExposeTime, setCardExposeTime] = React.useState(moment())

  const [updateAnniversaryCard] = useAnniversaryCardUpdateMutation()

  const createImage = ({
    image,
    anniversaryCard,
    anniversaryYears,
  }: {
    image: HTMLImageElement
    anniversaryCard: AnniversaryCardInfoFragment
    anniversaryYears: number
  }) => {
    const canvas = document.createElement('canvas')
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    const ctx = canvas!.getContext('2d')
    drawAnniversaryCard(ctx!, image, anniversaryCard, anniversaryYears)
    setImageUrls((prev) => [...prev, canvas.toDataURL()])
  }

  // init 时得到所有 data urls
  React.useEffect(() => {
    const image = new Image()
    image.onload = () => {
      // 绘制原始比例 canvas
      for (const anniversaryCard of anniversaryCards) {
        createImage({
          image,
          anniversaryCard,
          anniversaryYears,
        })
      }
    }
    image.crossOrigin = 'anonymous'
    image.src = backgroundUrl
  }, [])

  React.useEffect(() => {
    // 检查 imageUrls 是否 ready
    if (imageUrls.length === anniversaryCards.length) {
      setCurrentPage(0)
    }
  }, [imageUrls])

  React.useEffect(() => {
    if (isNumber(currentPage)) {
      setCardExposeTime(moment())
    }
  }, [currentPage])

  React.useEffect(() => {
    if (isNumber(currentPage)) {
      ref.current!.src = imageUrls[currentPage]
      if (!anniversaryCards[currentPage].isRead) {
        const category = traceEvent.anniversaryCard
        ga('event', category.events.show_card, {
          event_category: category.event_category,
          event_label: anniversaryCards[currentPage].cardCreator.email,
        })
        updateAnniversaryCard({
          variables: {
            input: {
              anniversaryCardId: anniversaryCards[currentPage].anniversaryCardId,
              content: anniversaryCards[currentPage].content,
              isRead: true,
            },
          },
        })
      }
    }
  }, [currentPage])

  React.useEffect(() => {
    if (postEditorReadyVisible && !isViewMode) {
      onAnniversaryCardModalClose()
    }
  }, [postEditorReadyVisible])

  if (!isNumber(currentPage)) {
    return null
  }

  const onNext = () => {
    const category = traceEvent.anniversaryCard
    if (currentPage < imageUrls.length - 1) {
      ga('event', category.events.expose_time_card, {
        event_category: category.event_category,
        event_label: `${anniversaryCards[currentPage].cardCreator.email}:${anniversaryCards[currentPage].anniversary}`,
        value: moment().diff(cardExposeTime, 'ms'),
      })
      setCurrentPage(currentPage + 1)
    }
    if (currentPage === imageUrls.length - 1) {
      ga('event', category.events.expose_time_card, {
        event_category: category.event_category,
        event_label: anniversaryCards[currentPage].cardCreator.email,
        value: moment().diff(cardExposeTime, 'ms'),
      })
      if (!isViewMode) {
        onPostEditorModalOpen()
      } else {
        onAnniversaryCardModalClose()
      }
    }
  }

  const onPrevious = () => {
    const category = traceEvent.anniversaryCard
    if (currentPage > 0) {
      ga('event', category.events.expose_time_card, {
        event_category: category.event_category,
        event_label: anniversaryCards[currentPage].cardCreator.email,
        value: moment().diff(cardExposeTime, 'ms'),
      })
      setCurrentPage(currentPage - 1)
    }
  }

  const footer = (
    <div className={s.buttons} style={{ backgroundColor: getBackgroundColor(anniversaryYears) }}>
      {currentPage !== 0 && (
        <a onClick={() => onPrevious()} style={{ color: getFillStyle(anniversaryYears), textDecoration: 'underline' }}>
          {t('anniversaryCard.previous')}
        </a>
      )}
      <a
        onClick={() => onNext()}
        style={{ color: getFillStyle(anniversaryYears), marginRight: 46, marginLeft: 20, textDecoration: 'underline' }}
      >
        {t(
          currentPage === imageUrls.length - 1
            ? isViewMode
              ? 'anniversaryCard.close'
              : 'anniversaryCard.share'
            : 'anniversaryCard.next'
        )}
      </a>
    </div>
  )

  return (
    <div className={s.modalPages}>
      {!isViewMode && postEditorPreReadyVisible && (
        <div className={s.modalLoading}>
          <Spin delay={300} size='large' />
        </div>
      )}
      <img style={{ width: imageWidth, height: imageHeight }} crossOrigin='anonymous' ref={ref} />
      {footer}
    </div>
  )
}
